import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import LanguageSwitcher from "./LanguageSwitcher";
import { useAuth } from "../firebase/AuthContext";
import { BouncingDots } from "../pages/Menu/AiChat/components/ChatComponents";

const OnWork = () => {
    const { currentUser, isAuthReady } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Language detection
    const getLangFromUrl = useCallback(() => {
        const params = new URLSearchParams(location.search);
        return params.get("lang") === "zh" ? "zh" : "en";
    }, [location.search]);

    const [language, setLanguage] = useState(getLangFromUrl);

    useEffect(() => {
        if (!currentUser) {
            // If user is not authenticated, redirect to login page
            navigate({ pathname: "/login", search: `?lang=${language}` });
        }
    }, [currentUser, navigate, language]);

    useEffect(() => {
        setLanguage(getLangFromUrl());
    }, [getLangFromUrl]);

    if (!currentUser || !isAuthReady) {
        return (
            <div className="flex h-screen bg-slate-100 dark:bg-dashboard-bg text-black dark:text-white font-sans">
                <LanguageSwitcher
                    language={language}
                    setLanguage={setLanguage}
                />
                <div className="flex-1 flex flex-col gap-8 items-center justify-center -mt-36">
                    <div className="flex flex-col gap-4 items-center text-center">
                        <div className="flex items-center gap-2">
                            <BouncingDots />
                            <span className="text-orange-500 text-lg font-semibold">
                                {language === "zh"
                                    ? "正在加載數據..."
                                    : "Loading data..."}
                            </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            {language === "zh"
                                ? "請稍候，正在為您準備個人資料面板。"
                                : "Please wait while we prepare your profile panel."}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-screen bg-dashboard-bg text-black font-sans">
            <Sidebar language={language} />
            <LanguageSwitcher language={language} setLanguage={setLanguage} />
            <div className="w-full bg-slate-100 dark:bg-dashboard-bg h-screen flex flex-col items-center justify-center relative overflow-hidden">
                {/* Main content */}
                <div className="relative z-10 text-center px-4 sm:px-6 max-w-sm sm:max-w-lg md:max-w-2xl mx-auto">
                    {/* T-Global Logo */}
                    <div className="mb-6 md:mb-8">
                        <img
                            src="/logo_tglobal.png"
                            alt="T-Global"
                            className="w-24 sm:w-28 md:w-32 h-auto mx-auto opacity-90"
                            onError={(e) => {
                                e.target.style.display = "none";
                            }}
                        />
                    </div>

                    {/* 404 Display */}

                    {/* Error message */}
                    <div className="mb-6 md:mb-8 bg-gray-200/50 dark:bg-white/10 backdrop-blur-md rounded-xl md:rounded-2xl border border-white/20 p-4 sm:p-6 md:p-8 shadow-xl">
                        <div className="flex items-center justify-center mb-4 md:mb-6">
                            <div className="mb-6 md:mb-8">
                                <div className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-bold text-orange-500 mb-3 md:mb-4">
                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g
                                            id="SVGRepo_bgCarrier"
                                            stroke-width="0"
                                        ></g>
                                        <g
                                            id="SVGRepo_tracerCarrier"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        ></g>
                                        <g id="SVGRepo_iconCarrier">
                                            {" "}
                                            <path
                                                d="M12 19C9.23858 19 7 16.7614 7 14M12 19C14.7614 19 17 16.7614 17 14M12 19V14M7 14V11.8571C7 11.0592 7 10.6602 7.11223 10.3394C7.31326 9.76495 7.76495 9.31326 8.33944 9.11223C8.66019 9 9.05917 9 9.85714 9H14.1429C14.9408 9 15.3398 9 15.6606 9.11223C16.2351 9.31326 16.6867 9.76495 16.8878 10.3394C17 10.6602 17 11.0592 17 11.8571V14M7 14H4M17 14H20M17 10L19.5 7.5M4.5 20.5L8 17M7 10L4.5 7.5M19.5 20.5L16 17M14 6V5C14 3.89543 13.1046 3 12 3C10.8954 3 10 3.89543 10 5V6H14Z"
                                                stroke="#FA7315"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            ></path>{" "}
                                        </g>
                                    </svg>
                                </div>
                                <div className="w-16 sm:w-20 md:w-24 h-1 bg-orange-500 mx-auto rounded-full"></div>
                            </div>
                        </div>

                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-600 dark:text-white mb-3 md:mb-4">
                            {language === "zh"
                                ? "頁面正在建設中"
                                : "Page is On Work"}
                        </h1>

                        <p className="text-base sm:text-lg text-slate-700 dark:text-slate-300 mb-4 md:mb-6 leading-relaxed px-2">
                            {language === "zh"
                                ? "抱歉，該頁面正在建設中。"
                                : "Sorry, the page you are looking for currently is on work."}
                        </p>
                    </div>

                    {/* Action buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
                        {/* <Link
                            to={`/?lang=${language}`}
                            className="group flex items-center gap-2 md:gap-3 bg-orange-500 hover:bg-orange-600 text-white px-6 sm:px-7 md:px-8 py-3 md:py-4 rounded-lg md:rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg shadow-orange-500/25 w-full sm:w-auto text-center justify-center"
                        >
                            <svg
                                className="w-4 h-4 md:w-5 md:h-5 group-hover:-translate-x-1 transition-transform duration-300"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                                />
                            </svg>
                            <span className="text-sm md:text-base">
                                {language === "zh"
                                    ? "返回首頁"
                                    : "Back to Home"}
                            </span>
                        </Link> */}

                        <Link
                            to={`/tg-product-assistant?lang=${language}`}
                            className="group flex items-center gap-2 md:gap-3 bg-black/10 hover:bg-black/20 dark:bg-white/10 dark:hover:bg-white/20 text-gray-800 dark:text-white border border-white/20 px-6 sm:px-7 md:px-8 py-3 md:py-4 rounded-lg md:rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 backdrop-blur-sm w-full sm:w-auto text-center justify-center"
                        >
                            <svg
                                className="w-4 h-4 md:w-5 md:h-5 group-hover:rotate-12 transition-transform duration-300"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                />
                            </svg>
                            <span className="text-sm md:text-base">
                                {language === "zh" ? "AI 對話" : "AI Chat"}
                            </span>
                        </Link>
                    </div>

                    {/* Footer text */}
                    <div className="mt-12 text-slate-500 text-sm">
                        <p>Horuseye PIMS © 2026</p>
                        <p className="mt-1">
                            {language === "zh"
                                ? "產品信息管理系統"
                                : "Product Information Management System"}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OnWork;
